import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Input, ViewChild } from '@angular/core';
// Controllers
import { IonModal, AlertController, ModalController, NavParams } from '@ionic/angular';
// Models
import { Directory } from '@mocca-technologies/model';


@Component({
  selector: 'app-directory-manager',
  templateUrl: './directory-manager.component.html',
  styleUrls: ['./directory-manager.component.scss'],
})
export class DirectoryManagerComponent {

  /**
   * ViewChild variables
   */
  @ViewChild(IonModal) modal: IonModal;

  /**
   * Input variables
   */
  @Input() directoryTree: Directory[];
  @Input() parentDirectoryTree: Directory[];
  @Input() selectedPath: string;

  /**
   * Variables
   */
  selectedDirectoryTree: Directory[] = [];
  selectedParentRef: Directory = null;
  selectedLevel = 1;

  treeControl = new FlatTreeControl<Directory>(
    (node) => node.level,
    (node) => true
  );

  openModalNewFolder: boolean = false;

  newFolderName: string;

  constructor(
    private alertController: AlertController,
    private modalCtrl: ModalController,
    public navParams: NavParams) {}

  // #region --> LifeCycle

  ngOnInit(){
    if(!this.selectedPath)
      this.selectedPath = "/";

    this.parentDirectoryTree?.length !== undefined ? this.loadFolders(this.selectedPath, this.parentDirectoryTree) : this.loadFolders(this.selectedPath);
  }

  // #endregion --> LifeCycle

  // #region --> Data Loading

  /**
   * This method is used from the parent component in order to load a custom directoryTree or load a path
   * @param customDirectoryTree
   * @param currentPath
   */
  loadFolders( currentPath?: string, customDirectoryTree?: Directory[]){
    if(customDirectoryTree.length !== 0){
      this.directoryTree = customDirectoryTree;
    }

    this.directoryTree.forEach((directory) => directory.level === this.selectedLevel ? this.selectedDirectoryTree.push(directory) : null);
    this.selectedPath = this.parsePath(currentPath);
    this.selectCurrentPath(this.selectedPath)
  }

  // #endregion --> Data Loading

  // #region --> Directory Methods

  /**
   * This method is used to select a path and expand its folders
   * @param currentPath
   */
  selectCurrentPath(currentPath: string){

    let directoryName = currentPath.split("/")[currentPath.split("/").length-1];
    let directoryLevel = currentPath.split("/").length-1;
    let directoryParentName = currentPath.split("/")[currentPath.split("/").length-2];
    let directoryParentLevel = currentPath.split("/").length-2;
    this.expandFolder(this.searchDirectoryByName(directoryName, directoryLevel, directoryParentName, directoryParentLevel));
  }

  /**
   * This method returns a path starting with "/"
   * @param path path that we want to parse
   * @returns the parsed path in case it doesn't start with "/"
   */
  parsePath(path: string, pathCutIndex?: number): string{

    let modifiedPath = !path.startsWith('/')?"/"+path:path;

    if(pathCutIndex){
      let splittedPath = modifiedPath.split('/').slice(1,pathCutIndex);
      modifiedPath = '';
      for(let i=0; i<splittedPath.length; i++){
        if (i===splittedPath.length-1){
          modifiedPath = modifiedPath+splittedPath[i];
        } else{
          modifiedPath = modifiedPath+splittedPath[i] + '/';
        }

      }
    }

    return modifiedPath;
  }

  /**
   * This method searches a directory in the tree with its name and level and its parent's name and level
   * @param directoryName
   * @param directoryLevel
   * @param directoryParentName
   * @param directoryParentLevel
   * @returns
   */
  searchDirectoryByName(directoryName: string, directoryLevel: number, directoryParentName: string, directoryParentLevel: number): Directory{
    let selectedDirectory: Directory = null;
    if(directoryParentName===""&&directoryParentLevel===0){
      this.directoryTree.forEach((directory) => {
        if(directory.name===directoryName&&directory.level===directoryLevel){
          selectedDirectory=directory
        }
      })
    } else{
      this.directoryTree.forEach((directory) => {
        (directory.name===directoryName&&directory.level===directoryLevel&&directory.parentRef?.name===directoryParentName&&directory.parentRef?.level===directoryParentLevel)?selectedDirectory=directory:selectedDirectory=null;
      })
    }
    return selectedDirectory;
  }

  /**
   * This method searches a directory in the tree with its level and its parent's name and level
   * @param directoryLevel
   * @param directoryParentName
   * @param directoryParentLevel
   * @returns
   */
  searchDirectoryByLevel(directoryLevel: number, directoryParentName: string, directoryParentLevel: number): Directory[]{
    let selectedDirectories: Directory[] = [];

    this.directoryTree.forEach((directory) => {
      (directory.level===directoryLevel&&directory.parentRef?.name===directoryParentName&&directory.parentRef?.level===directoryParentLevel)?selectedDirectories.push(directory):null;
    })

    return selectedDirectories;
  }

  /**
   * This method is used to expand a Directory
   * @param expandedDirectory
   */
  expandFolder(expandedDirectory: Directory){
    //In case the directory is a final directory with items
    if(expandedDirectory!==null){
      this.selectedLevel = expandedDirectory.level+1;
      this.selectedParentRef = expandedDirectory;

      this.selectedPath = this.parsePath(this.selectedPath,expandedDirectory.level)+"/"+expandedDirectory.name;

      let newSelectedDirectoryTree: Directory[] = [];
      newSelectedDirectoryTree = [...this.searchDirectoryByLevel(this.selectedLevel, this.selectedParentRef.name, this.selectedParentRef.level)]
      this.selectedDirectoryTree = [...newSelectedDirectoryTree];
    }
  }

  /**
   * This method is used to move to the parent folder.
   */
  async moveUpSelectedFolder(){
    if(this.selectedLevel===1){
      const alert = await this.alertController.create({
        header: 'Unable to change directory',
        message: 'You are in the parent directory, choose a created directory or create a new one.',
        buttons: [
        {
          text: 'OK',
          role: 'cancel',
          handler: () => {
            this.cancelPath();
            },
          },
        ],
      });
      await alert.present();
    } else{
      this.selectedPath = this.parsePath(this.selectedPath,this.selectedLevel-1)+"/";
      this.selectedLevel = this.selectedLevel-1;
      this.selectedDirectoryTree = [];

      this.directoryTree.forEach((directory)=>directory.level===this.selectedLevel&&directory.parentRef===this.selectedParentRef.parentRef?this.selectedDirectoryTree.push(directory):null);
      this.selectedParentRef=this.selectedParentRef.parentRef;
    }

  }

  /**
   * This method triggers the form which introduces a new folder
   */
  async addNewFolder(){
    this.openModalNewFolder = true;
  }

  // #endregion --> Directory Methods

  // #region --> Modal Methods

  /**
   * This method is used if we don't want to add a folder
   */
  cancel() {
    this.modal.dismiss(null, 'cancel');
    this.openModalNewFolder=false;
    this.newFolderName='';
  }

  /**
   * This method is used to confirm and add a folder
   */
  async confirm() {
    this.modal.dismiss(null, 'confirm');
    this.openModalNewFolder=false;
    const alert = await this.alertController.create({
      header: 'Confirm new folder',
      message: 'Do you want to add the folder ' + this.newFolderName + '?',
      buttons: [
      {
        text: 'Cancel',
        role: 'cancel',
        handler: () => {
          this.newFolderName='';
        },
      },
      {
        text: 'Yes',
        handler: () => {
          let parentRef: Directory = null;
          this.directoryTree.forEach((directory) => {
            if(directory.name === this.selectedPath.split('/').slice(-1)[0]){
              parentRef = directory;

            }});
            if(parentRef?.pseudonym===''){
              parentRef=null;
            }
          const newFolder: Directory = {
            name: this.newFolderName,
            level: this.selectedLevel,
            pseudonym: this.newFolderName,
            selected: false,
            isExpanded: false,
            badgeValue: 0,
            parentRef: parentRef,
            items: [],
          };

          this.directoryTree.push(newFolder);
          this.expandFolder(newFolder);
          this.newFolderName = "";
          },
        },
      ],
    });
    await alert.present();
  }

  /**
   * This method is used to cacnel the path, but we mantain created folders.
   * @returns
   */
  cancelPath(){
    const data = {
      'directoryTreeChild': this.directoryTree,
    }
    return this.modalCtrl.dismiss(data, 'cancel');
  }

  /**
   * This method is used to confirm the path and mantains the created folders.
   */
  async confirmPath(){
    const alert = await this.alertController.create({
      header: 'Confirm path',
      message: 'Do you want to add this Operation Request to the path: ' + this.selectedPath + '?',
      buttons: [
      {
        text: 'Cancel',
        role: 'cancel',
        handler: () => {
          this.cancelPath();
        },
      },
      {
        text: 'Yes',
        handler: () => {
          const data = {
            'path': this.selectedPath,
            'directoryTreeChild': this.directoryTree,
          }
          return this.modalCtrl.dismiss(data, 'confirm');
          },
        },
      ],
    });
    await alert.present();
  }

  // #endregion --> Modal Methods

}
